import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import moment from 'moment';
import { toast } from 'react-toastify';
import { IntlShape, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { translate } from 'containers/services/translate';
import messages from 'containers/RecapManagerPage/messages';
import { COLORS } from 'constants/design';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import { get, isEmpty, uniqueId } from 'lodash';
import { getValueByCode } from 'containers/CourtManagerLegalProceedings/Provision/utils';
import { Listings } from 'containers/AppContainer/typings';
import { PROVISION_INTERVENANT_FIELDS } from 'containers/RecapManagerPage/constants';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import currentUserStorage from 'containers/services/security/current-user-storage';

const StyledDiv = styled.div`
  width: 576px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .select {
    width: 90%;
    margin-bottom: 33px;
    font-size: 13px;
  }
`;
const StyledDialogActions = styled(DialogActions)`
  display: flex;
  padding: 20px 30px;
  justify-content: space-between;

  .moveButton {
    border: 1px solid ${COLORS.YELLOW};
    background-color: ${COLORS.YELLOW};
    color: ${COLORS.LIMED_SPURCE};
    &:hover {
      background-color: ${COLORS.YELLOW};
    }
  }

  .Mui-disabled {
    opacity: 0.6;
  }
`;

export interface ProvisionIntervenantDialogProps {
  intl: IntlShape;
  open: boolean;
  onClose: Function;
  intervenants?: any;
  listings?: Listings;
  requestValidateProvisionIntervenant?: Function;
  riskId: string | undefined;
  currentIntervenant?: any;
  enableValidation?: boolean;
  claimNumber?: string;
  setUpdateCurrentRisk?: Function;
  claimId: number;
}

const ProvisionIntervenantDialog: React.FC<ProvisionIntervenantDialogProps> = (
  props,
): JSX.Element => {
  const {
    onClose,
    open,
    intervenants,
    requestValidateProvisionIntervenant,
    riskId,
    currentIntervenant = null,
    claimNumber,
  } = props;

  /* istanbul ignore next */
  const handleValidation = () => {
    props.setUpdateCurrentRisk && props.setUpdateCurrentRisk(false);
    requestValidateProvisionIntervenant &&
      requestValidateProvisionIntervenant(
        {
          riskId,
          reasonCode: '2',
          claimId: props.claimId,
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
          date: moment().toISOString(true),
          typeProvision: 5,
          amount: provisionAmount,
          intervenantCode:
            get(assignedIntervenant, 'contractorCode') ||
            get(currentIntervenant, 'intervenantCode'),
          intervenantTypeCode:
            get(assignedIntervenant, 'contractorTypeCode') ||
            get(currentIntervenant, 'intervenantTypeCode'),
          claimNumber,
          createdBy: currentUserStorage?.get()?.username,
          isManualEvaluation: false,
        },
        successCallBack,
      );
    onClose(false);
    setAssignedIntervenant(null);
    setProvisionAmount(null);
  };

  const successCallBack = () => {
    toast.success(
      translate(messages.editProvisionIntervenantSuccessMessage, props),
    );
    props.setUpdateCurrentRisk && props.setUpdateCurrentRisk(true);
  };

  const [assignedIntervenant, setAssignedIntervenant] = useState<string | null>(
    null,
  );

  const [provisionAmount, setProvisionAmount] = useState<number | null>(null);

  const handleChange = (
    event: React.ChangeEvent<{
      value: any;
      name?: string;
    }>,
  ) => {
    const { value, name } = event.target;
    if (name === PROVISION_INTERVENANT_FIELDS.INTERVENANT) {
      setAssignedIntervenant({ ...value });
    } else if (name === PROVISION_INTERVENANT_FIELDS.AMOUNT) {
      setProvisionAmount(Number(value) as number);
    }
  };

  return (
    <Dialog
      onClose={() => {
        onClose(false);
        setAssignedIntervenant(null);
        setProvisionAmount(null);
      }}
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <DialogTitle id="simple-dialog-title">
        {translate({ ...messages.editProvisionIntervenantDialogTitle }, props)}
      </DialogTitle>
      <DialogContent>
        <StyledDiv>
          <FormControl className="select">
            <TextField
              type="number"
              fullWidth
              label={translate(
                { ...messages.editProvisionIntervenantAmount },
                props,
              )}
              name={PROVISION_INTERVENANT_FIELDS.AMOUNT}
              value={provisionAmount}
              onChange={handleChange}
            />
          </FormControl>
          {intervenants && (
            <FormControl className="select">
              <InputLabel>
                {translate({ ...messages.editProvisionIntervenant }, props)}
              </InputLabel>
              <Select
                name={PROVISION_INTERVENANT_FIELDS.INTERVENANT}
                value={get(assignedIntervenant, 'contractorCode')}
                onChange={handleChange}
                labelWidth={60}
              >
                {!isEmpty(intervenants) &&
                  intervenants.map((val: any) => (
                    <MenuItem key={uniqueId('intervenant')} value={val}>
                      {getValueByCode(
                        val.contractorCode,
                        get(props.listings, 'intervenants', []),
                      )}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
        </StyledDiv>
      </DialogContent>
      <StyledDialogActions>
        <SecondaryButton
          onClick={() => {
            onClose(false);
            setAssignedIntervenant(null);
            setProvisionAmount(null);
          }}
          disableElevation
          title={translate({ ...messages.assignmentDialogCancelButton }, props)}
        />
        <PrimaryButton
          onClick={handleValidation}
          disableElevation
          disabled={
            !Boolean(
              (assignedIntervenant || currentIntervenant) &&
                provisionAmount !== null,
            )
          }
          title={translate(
            { ...messages.assignmentDialogValidationButton },
            props,
          )}
        />
      </StyledDialogActions>
    </Dialog>
  );
};

ProvisionIntervenantDialog.defaultProps = {
  open: false,
};

export default injectIntl(ProvisionIntervenantDialog);
